import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { billingAPI, clientJourneysAPI, jobVerticalAPI, paymentRecordsAPI } from '../services/api';
import PaymentsPanel from './PaymentsPanel';
import { MedicalPaymentsPanel } from './ClientJourneyPanel';
import JobFeesPanel from './JobFeesPanel';
import InvoicePick, { invoicePickPayload, invoicePickReady } from './InvoicePick';
import './Billing.css';

// The CFO's Payments desk. Founder rule, 19 Oct 2026: only the CFO records a
// payment. The CFO role has no general access to client records, so this page
// gives just enough to do that: a queue of entries waiting for a check (added by
// clients on their own links, or logged by staff), and a name / ID search that
// opens one client's payment ledger. Invoice payments stay on the invoice, in
// Billing Documents.

const KIND_LABEL = { student: 'Student', medical: 'Medical', job: 'Job seeker' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const errText = (e, fallback) => {
  const d = e?.response?.data || {};
  return d.error || d.detail || Object.values(d).flat()[0] || fallback;
};

async function downloadMedicalReceipt(row) {
  const res = await clientJourneysAPI.receipt(row.client_id, row.id);
  const type = res.headers?.['content-type'] || 'application/octet-stream';
  const url = URL.createObjectURL(new Blob([res.data], { type }));
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function Queue({ rows, loading, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [picking, setPicking] = useState(null); // key of a row choosing its invoice
  const [pick, setPick] = useState({ invoice: '', reason: '' });
  const act = async (row, op) => {
    const key = `${row.kind}-${row.id}`;
    if (op === 'accept' && row.needs_invoice && picking !== key) {
      setPicking(key); setPick({ invoice: '', reason: '' });
      return;
    }
    let note = '';
    if (op === 'reject') {
      note = (window.prompt('Why is this payment rejected? The client or staff member sees this.') || '').trim();
      if (!note) return;
    }
    setBusy(`${row.kind}-${row.id}`); setErr('');
    try {
      if (row.kind === 'student') {
        await (op === 'accept'
          ? paymentRecordsAPI.accept(row.id, row.needs_invoice ? invoicePickPayload(pick) : {})
          : paymentRecordsAPI.reject(row.id, note));
        setPicking(null);
      } else {
        await clientJourneysAPI.paymentOp(row.client_id, row.id, op, op === 'reject' ? { note } : {});
      }
      onChanged();
    } catch (e) {
      setErr(errText(e, 'That did not work.'));
    } finally { setBusy(null); }
  };
  return (
    <div className="billing-panel" style={{ marginBottom: 20 }}>
      <div className="billing-panel-head"><h3>Waiting for your check ({rows.length})</h3></div>
      <p className="billing-hint">
        Accepting an entry records the payment as received. Rejecting sends it back with your note.
      </p>
      {err && <div className="billing-error">{err}</div>}
      {loading ? <div className="billing-loading">Loading…</div> : (
        <table className="billing-table">
          <thead>
            <tr><th>Client</th><th>For</th><th className="num">Amount</th><th>Added by</th><th>Added</th><th /></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="billing-empty">Nothing waiting.</td></tr>}
            {rows.map((r) => {
              const key = `${r.kind}-${r.id}`;
              return (
                <React.Fragment key={key}>
                <tr>
                  <td>{r.client_name}<span className="billing-sub">{KIND_LABEL[r.kind]} · {r.client_ref}</span></td>
                  <td>{r.description}{r.paid_on && <span className="billing-sub">paid {fmt(r.paid_on)}</span>}</td>
                  <td className="num">{r.currency} {Number(r.amount).toFixed(2)}</td>
                  <td>{r.added_by || '—'}</td>
                  <td>{fmt(r.created_at)}</td>
                  <td className="billing-row-actions">
                    {r.receipt_url && <a href={r.receipt_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, marginRight: 4 }}>Receipt</a>}
                    {r.kind === 'medical' && r.has_receipt && (
                      <button type="button" onClick={() => downloadMedicalReceipt(r).catch(() => setErr('Could not open the receipt.'))}>Receipt</button>
                    )}
                    <button type="button" disabled={busy === key || (picking === key && !invoicePickReady(pick))}
                      onClick={() => act(r, 'accept')}>{picking === key ? 'Confirm & issue receipt' : 'Accept'}</button>
                    <button type="button" disabled={busy === key} onClick={() => act(r, 'reject')}>Reject</button>
                  </td>
                </tr>
                {picking === key && (
                  <tr><td colSpan={6} className="billing-row-actions" style={{ textAlign: 'left' }}>
                    <span className="billing-sub" style={{ display: 'inline', marginRight: 6 }}>Billing receipt against:</span>
                    <InvoicePick studentId={r.client_id} currency={r.currency} value={pick} onChange={setPick} />
                    <button type="button" onClick={() => setPicking(null)}>Cancel</button>
                  </td></tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function JobLedger({ clientId }) {
  const [charges, setCharges] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await jobVerticalAPI.charges.list({ job_client: clientId });
      setCharges(r.data?.results ?? r.data ?? []);
      setErr('');
    } catch (e) { setErr(errText(e, 'Could not load the fees.')); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  if (!charges) return <div className="billing-hint">{err || 'Loading fees…'}</div>;
  return <JobFeesPanel client={{ id: clientId }} charges={charges} schedule={[]} onChanged={load} ledgerOnly />;
}

function ClientFinder({ onChanged }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      setSearching(true);
      billingAPI.paymentDesk.clients(q.trim())
        .then((r) => setResults(r.data?.results || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="billing-panel">
      <div className="billing-panel-head"><h3>Record a payment for a client</h3></div>
      <p className="billing-hint">
        Find the student, medical client or job seeker by name or ID, then record what they paid on their ledger.
        For a payment against a VisionExploit invoice, record it on the invoice in{' '}
        <Link to="/billing">Billing Documents</Link> so the receipt and balance follow.
      </p>
      <div className="billing-form" style={{ marginBottom: 12 }}>
        <label>Client
          <input placeholder="Name or ID, e.g. Moyo or VEC-MED-2026-0012" value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); }} />
        </label>
        {searching && <div className="billing-sub">Searching…</div>}
        {!picked && results.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
            {results.map((c) => (
              <button key={`${c.kind}-${c.id}`} type="button" className="btn-secondary btn-sm" style={{ textAlign: 'left' }}
                onClick={() => setPicked(c)}>
                {c.name} · {c.ref} <span className="billing-sub" style={{ display: 'inline' }}>({KIND_LABEL[c.kind]})</span>
              </button>
            ))}
          </div>
        )}
        {!picked && !searching && q.trim().length >= 2 && results.length === 0 && (
          <div className="billing-sub">No client found.</div>
        )}
      </div>
      {picked && (
        <div>
          <strong>{picked.name}</strong> <span className="billing-sub" style={{ display: 'inline' }}>{KIND_LABEL[picked.kind]} · {picked.ref}</span>
          {picked.kind === 'student' && <PaymentsPanel studentId={picked.id} onChanged={onChanged} />}
          {picked.kind === 'medical' && <MedicalPaymentsPanel clientId={picked.id} onChanged={onChanged} />}
          {picked.kind === 'job' && <JobLedger clientId={picked.id} />}
        </div>
      )}
    </div>
  );
}

export default function PaymentsDesk() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await billingAPI.paymentDesk.queue();
      setRows(r.data?.pending || []);
      setErr('');
    } catch (e) {
      setErr(errText(e, 'Could not load the payments waiting for you.'));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="billing-page">
      <h1>Record payments</h1>
      <p className="billing-hint">
        Only the CFO or Chairman records payments. Everyone else can log one, and it waits here until you check it.
      </p>
      {err && <div className="billing-error">{err}</div>}
      <Queue rows={rows} loading={loading} onChanged={load} />
      <ClientFinder onChanged={load} />
    </div>
  );
}
